import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, MapPin } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { TicketButton } from "./TicketButton";
import { cad, dayNum, monthShort, time12, weekday } from "../lib/format";
import { imgSrc } from "../lib/api";

export const ConcertCard = ({ concert: c, index = 0 }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`concert-card-${c.slug}`}
    className="concert-card group flex flex-col overflow-hidden rounded-[22px] border border-white/10 bg-[#12141C]"
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img src={imgSrc(c.image)} alt={`${c.artist} performing live`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-[#12141C]/20 to-transparent" />
      <div className="spotlight absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
      <div className="absolute left-4 top-4 rounded-2xl bg-[#0B0C10]/80 px-3 py-2 text-center backdrop-blur-md">
        <div className="font-mono text-[11px] uppercase tracking-widest text-sunset">{monthShort(c.date)}</div>
        <div className="font-display text-3xl font-extrabold leading-none text-white">{dayNum(c.date)}</div>
      </div>
      <div className="absolute right-4 top-4"><StatusBadge status={c.is_past ? "Past" : c.status} testId={`concert-status-${c.slug}`} /></div>
    </div>
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#C9A0F0]">{c.genre}</p>
        <h3 className="mt-1 font-display text-3xl font-extrabold uppercase leading-[0.95] text-white">{c.artist}</h3>
        {c.supporting_acts?.length > 0 && <p className="mt-1 text-sm text-zinc-400">with {c.supporting_acts.join(", ")}</p>}
      </div>
      <ul className="space-y-1.5 text-sm text-zinc-300">
        <li className="flex items-center gap-2"><Clock size={14} className="text-sunset" />{weekday(c.date)} · Show {time12(c.show_time)}</li>
        <li className="flex items-center gap-2"><MapPin size={14} className="text-sunset" />{c.venue}</li>
      </ul>
      <div className="mt-auto flex items-end justify-between border-t border-white/10 pt-4">
        <div data-testid={`concert-price-${c.slug}`}>
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">From</p>
          <p className="font-display text-2xl font-extrabold text-white">{cad(c.price_from) ?? <span className="text-base font-semibold text-zinc-300">Price TBA</span>}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Link to={`/concerts/${c.slug}`} data-testid={`concert-details-btn-${c.slug}`} className="btn-ghost justify-center">View Details</Link>
        <TicketButton concert={c} testId={`concert-tickets-btn-${c.slug}`} className="justify-center" />
      </div>
    </div>
  </motion.article>
);
